import { useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { Button, Checkbox, Label, Textarea } from 'flowbite-react';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import Select, { MultiValue } from 'react-select';
import { Dropdown } from 'shared-utils';
import { getPositionsForInvite, registerForDrive } from 'src/apis/drive';
import { useQuery } from 'react-query';
import { useUserContext } from 'src/context/UserProvider';
import { HiOutlinePlus } from 'react-icons/hi';
import TermsOfPlacementDrivesModal from 'src/components/recruiterPositions/recruiterTypes/Modals/TermsOfPlacementDrivesModal';
import { Drawer } from 'src/components/shared/Drawer';
import { useToast } from 'src/context/ToastProvider';
import { useGetAllDrivesQuery } from 'src/hooks/drives.hooks';
import DriveFormForRecruiter from 'src/pages/newRecruiterFlow/DriveFormForRecruiter';
import { CampusUserWithApplication, RecruiterFormData, UserRole } from 'types';

interface Props {
	jobseekers: CampusUserWithApplication[];
	showHeading?: boolean;
	onFinish?: () => void;
}

const CampusInviteJobSeekerForDrive = ({ jobseekers, onFinish, showHeading = true }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
	const [showHiringDetailsForm, setShowHiringDetailsForm] = useState<boolean>(false);
	const { isAccountManagerLoggedIn } = useUserContext();
	// Account managers can push the candidate to positions that already exist for the drive instead of creating a new one.
	const [existingPositionIds, setExistingPositionIds] = useState<string[]>([]);
	const [createNewPosition, setCreateNewPosition] = useState<boolean>(false);
	const [hasUnregisteredDrivesSelected, setHasUnregisteredDrivesSelected] = useState<boolean>(false);
	const [formData, setFormData] = useState<{ drives: string[]; termsAccepted: boolean; remarks: string }>({
		drives: [],
		termsAccepted: false,
		remarks: '',
	});

	const { data, isLoading, refetch } = useGetAllDrivesQuery();

	const drivesOptions = data?.drives?.map(e => ({
		label: e.driveEndDate && e?.driveEndDate !== "" && e?.driveEndDate !== null
			? `${dayjs(e.driveDate).format('Do MMM')} - ${dayjs(e.driveEndDate).format('Do MMM, YYYY')} ${e.city}`
			: `${dayjs(e.driveDate).format('Do MMM, YYYY')} ${e.city}`,
		value: e._id,
	}));

	const { data: invitePositionsData, isLoading: isLoadingInvitePositions } = useQuery(
		['getPositionsForInvite', formData.drives],
		() => getPositionsForInvite(formData.drives),
		{ enabled: !!isAccountManagerLoggedIn && formData.drives.length > 0 },
	);
	const invitePositions = invitePositionsData?.positions ?? [];
	const positionOptions = invitePositions.map(position => ({
		value: position._id,
		label: `${position.company?.name || 'Company'} ${position.title}`,
		company: position.company?.name || 'Company',
		role: `${String(position.title || '').replace(/_/g, ' ')}${position.positionLabel ? ` (${position.positionLabel})` : ''}`,
	}));

	useEffect(() => {
		// Drop picks that belong to a drive that is no longer selected.
		setExistingPositionIds(prev => prev.filter(id => invitePositions.some(position => position._id === id)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [invitePositionsData]);

	const registerForDriveFn = useMutation(['registerForDrive'], registerForDrive, {
		onSuccess(data) {
			toast({
				title: 'Drive Invite sent successfully',
				type: 'success',
			});
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			refetch();
			onFinish && onFinish();
		},
		onError(error: Error) {
			console.log('error:', error);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (recruiterFormData?: RecruiterFormData) => {
		const needsNewPositionForm =
			!recruiterFormData && (createNewPosition || (hasUnregisteredDrivesSelected && !(isAccountManagerLoggedIn && existingPositionIds.length)));
		if (needsNewPositionForm) {
			setShowHiringDetailsForm(true);
		} else {
			registerForDriveFn.mutate({
				existingPositionIds: isAccountManagerLoggedIn ? existingPositionIds : undefined,
				selectedDrives: formData?.drives,
				jobseekers: jobseekers.map(e => e?._id),
				type: UserRole.RECRUITER,
				formData: recruiterFormData,
				remarks: formData?.remarks,
			});
		}
	};

	useEffect(() => {
		const unregisteredDrives = formData?.drives?.filter(
			e => !data?.drives?.find(drive => drive._id === e)?.isRegistered,
		);
		if (unregisteredDrives?.length) {
			setHasUnregisteredDrivesSelected(true);
		} else {
			setHasUnregisteredDrivesSelected(false);
		}
	}, [formData]);

	return (
		<>
			<form
				className="px-1 flex flex-col gap-[20px]"
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				{showHeading ? (
					<p className="font-inter text-[20px] font-[600] leading-[28px] mt-3">{'Placement Drive Invite '}</p>
				) : null}
				<div className="block">
					<div className="mb-[8px] flex justify-between items-end">
						<div>
							<Label>{'Drive Date And Location '}</Label>
							<span className="text-[red]">*</span>
						</div>
					</div>
					<div>
						<Select
							isMulti
							required
							isClearable
							menuPosition="fixed"
							isLoading={isLoading}
							options={drivesOptions}
							classNamePrefix="flowbite-select"
							onChange={(value: MultiValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, drives: value?.map(e => e.value) }));
							}}
							value={drivesOptions?.filter(item => formData?.drives?.includes(item.value))}
						/>
					</div>
				</div>

				{isAccountManagerLoggedIn && formData.drives.length ? (
					<div className="block">
						<div className="mb-[8px] flex items-end justify-between">
							<Label>{'Positions in this drive '}</Label>
							{invitePositions.length ? (
								<button
									type="button"
									className="text-xs font-medium text-[#6698FA] underline"
									onClick={() =>
										setExistingPositionIds(
											existingPositionIds.length === invitePositions.length ? [] : invitePositions.map(position => position._id),
										)
									}
								>
									{existingPositionIds.length === invitePositions.length ? 'Clear all' : 'Select all'}
								</button>
							) : null}
						</div>
						<Select
							isMulti
							isClearable
							closeMenuOnSelect={false}
							menuPosition="fixed"
							isLoading={isLoadingInvitePositions}
							placeholder={invitePositions.length ? 'Search and select positions' : 'No open positions in the selected drive(s)'}
							noOptionsMessage={() => 'No positions found'}
							options={positionOptions}
							classNamePrefix="flowbite-select"
							value={positionOptions.filter(option => existingPositionIds.includes(option.value))}
							onChange={(value: MultiValue<Dropdown>) => setExistingPositionIds(value.map(option => option.value))}
							formatOptionLabel={(option: any) => (
								<div className="leading-tight">
									<p className="text-sm font-semibold">{option.company}</p>
									<p className="text-xs text-gray-500">{option.role}</p>
								</div>
							)}
							filterOption={(option: any, input: string) =>
								`${option.data.company} ${option.data.role}`.toLowerCase().includes(input.toLowerCase())
							}
						/>
						<label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-[#555770]">
							<Checkbox color="blue" checked={createNewPosition} onChange={e => setCreateNewPosition(e.target.checked)} />
							<HiOutlinePlus className="h-4 w-4" />
							Create a new position for this drive
						</label>
					</div>
				) : null}

				<div className="block">
					<div className="mb-[8px] flex justify-between items-end">
						<div>
							<Label>{'Your message '}</Label>
						</div>

						<span className="italic text-xs text-[#6B7280] ">{formData?.remarks?.length + ' / ' + 640}</span>
					</div>
					<Textarea
						name="message"
						placeholder="Write message here ..."
						value={formData?.remarks}
						maxLength={640}
						onChange={e => {
							setFormData(prev => ({ ...prev, remarks: e.target.value }));
						}}
						className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[100px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					/>
				</div>

				<div className="flex items-start gap-2 text-[#555770] text-sm font-[500]">
					<Checkbox
						color="blue"
						id="terms"
						checked={formData?.termsAccepted}
						className="mt-1 cursor-pointer"
						onChange={e => {
							setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }));
						}}
					/>
					<label htmlFor="terms">
						By sending these invites, you agree to the{' '}
						<span
							className="text-[#6698FA] cursor-pointer underline"
							onClick={() => {
								setShowTermsModal(true);
							}}
						>
							terms of the placement drive.
						</span>
					</label>
				</div>
				<Button
					type="submit"
					color="purplePlaced"
					className="mx-auto w-full"
					isProcessing={registerForDriveFn.isLoading}
					disabled={
						registerForDriveFn.isLoading ||
						!formData?.termsAccepted ||
						!formData?.drives?.length ||
						(!!isAccountManagerLoggedIn && !existingPositionIds.length && !createNewPosition && !hasUnregisteredDrivesSelected)
					}
				>
					Send Invite to Candidate
				</Button>
			</form>

			<TermsOfPlacementDrivesModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />

			<Drawer
				fullWidth={true}
				minHeight="100vh"
				isModal={!isMobile}
				isOpen={showHiringDetailsForm}
				header={
					<div className="p-5 pb-0 text-[#1F3343] font-[600] text-xl">
						<h3>Placement drive hiring details</h3>
						<p className="text-[#3568D4] font-[400] text-[14px]">
							Now that candidates have been invited, please provide a few details about the hiring process.
						</p>
					</div>
				}
				modalMaxWidth={'760px'}
				body={
					<DriveFormForRecruiter
						isModal={true}
						onFinish={formData => {
							handleSubmit(formData);
						}}
					/>
				}
				onClose={() => {
					setShowHiringDetailsForm(false);
				}}
			/>
		</>
	);
};

export default CampusInviteJobSeekerForDrive;
